import {
  Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

export interface Bar {
  left: string;
  height: string;
  heightPct: number;
  cardLeft: string;
  cardBottom: string;
  barDelay: string;
  cardDelay: string;
  label: string;
  iconType: 'profile' | 'resume' | 'test' | 'mic' | 'send' | 'check';
}

export interface Particle {
  size: number;
  left: string;
  top: string;
  color: string;
  duration: string;
  delay: string;
}

export interface Confetti {
  left: string;
  color: string;
  size: number;
  duration: string;
  delay: string;
  shape: 'circle' | 'rect';
}

@Component({
  selector: 'app-hero',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './hero.component.html',
  styleUrls: ['./hero.component.scss', '../career-roadmap/career-roadmap.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeroComponent implements OnInit, OnDestroy {

  bars: Bar[] = [
    { left: '4%',  height: '10%', heightPct: 10, cardLeft: '2%',  cardBottom: 'calc(10% + 12px)', barDelay: '0s',    cardDelay: '0.4s',  label: 'Create Profile', iconType: 'profile' },
    { left: '18%', height: '20%', heightPct: 20, cardLeft: '16%', cardBottom: 'calc(20% + 12px)', barDelay: '0.15s', cardDelay: '0.55s', label: 'Build Resume',    iconType: 'resume'  },
    { left: '32%', height: '30%', heightPct: 30, cardLeft: '30%', cardBottom: 'calc(30% + 12px)', barDelay: '0.3s',  cardDelay: '0.7s',  label: 'Practice Tests',  iconType: 'test'    },
    { left: '46%', height: '40%', heightPct: 40, cardLeft: '44%', cardBottom: 'calc(40% + 12px)', barDelay: '0.45s', cardDelay: '0.85s', label: 'AI Interviews',   iconType: 'mic'     },
    { left: '60%', height: '50%', heightPct: 50, cardLeft: '58%', cardBottom: 'calc(50% + 12px)', barDelay: '0.6s',  cardDelay: '1s',    label: 'Apply for Jobs',  iconType: 'send'    },
    { left: '74%', height: '60%', heightPct: 60, cardLeft: '72%', cardBottom: 'calc(60% + 12px)', barDelay: '0.75s', cardDelay: '1.15s', label: 'Get Hired! 🎉',   iconType: 'check'   },
  ];

  particles: Particle[] = [];
  confetti: Confetti[] = [];

  characterLeft   = '4%';
  characterBottom = '10%';
  isCelebrating   = false;   // ← swap image + show overlay when true

  private currentStep   = 0;
  private climbTimeout: ReturnType<typeof setTimeout> | null = null;

  private particleColors  = ['#c7d2fe', '#a5b4fc', '#fbbf24', '#6ee7b7', '#f9a8d4'];
  private confettiColors  = ['#6366f1', '#8b5cf6', '#fbbf24', '#34d399', '#f472b6', '#60a5fa', '#fb923c'];

  constructor(private cdr: ChangeDetectorRef, private ngZone: NgZone) {}

  ngOnInit(): void {
    this.generateParticles();
    this.generateConfetti();
    this.ngZone.runOutsideAngular(() => {
      this.climbTimeout = setTimeout(() => this.startClimbing(), 1200);
    });
  }

  ngOnDestroy(): void {
    if (this.climbTimeout) clearTimeout(this.climbTimeout);
  }

  private generateParticles(): void {
    const positions = [
      { left: '8%', top: '10%' }, { left: '20%', top: '22%' }, { left: '30%', top: '40%' },
      { left: '42%', top: '55%' }, { left: '50%', top: '70%' }, { left: '60%', top: '15%' },
      { left: '70%', top: '35%' }, { left: '80%', top: '50%' }, { left: '88%', top: '25%' }
    ];
    const sizes = [6, 9, 12, 6, 9, 12, 6, 9, 12];
    this.particles = positions.map((pos, i) => ({
      size: sizes[i], left: pos.left, top: pos.top,
      color: this.particleColors[i % this.particleColors.length],
      duration: `${3 + (i % 3)}s`, delay: `${(i * 0.4).toFixed(1)}s`
    }));
  }

  private generateConfetti(): void {
    this.confetti = Array.from({ length: 24 }, (_, i) => ({
      left:     `${Math.round(5 + (i / 23) * 90)}%`,
      color:    this.confettiColors[i % this.confettiColors.length],
      size:     6 + (i % 3) * 3,
      duration: `${0.8 + (i % 5) * 0.3}s`,
      delay:    `${(i * 0.06).toFixed(2)}s`,
      shape:    (i % 3 === 0) ? 'rect' : 'circle'
    }));
  }

  private startClimbing(): void {
    const bar = this.bars[this.currentStep];
    const isLast = this.currentStep === this.bars.length - 1;

    this.ngZone.run(() => {
      this.characterLeft   = `calc(${bar.left} + 2%)`;
      this.characterBottom = bar.height;
      this.isCelebrating   = false;
      this.cdr.markForCheck();
    });

    this.currentStep++;

    if (this.currentStep < this.bars.length) {
      this.climbTimeout = setTimeout(() => this.startClimbing(), 1000);
    } else {
      // She reached the top! Switch to celebration after transition completes
      this.climbTimeout = setTimeout(() => {
        this.ngZone.run(() => {
          this.isCelebrating = true;
          this.cdr.markForCheck();
        });

        // After celebration, reset and restart
        this.climbTimeout = setTimeout(() => {
          this.ngZone.run(() => {
            this.isCelebrating   = false;
            this.characterLeft   = `calc(${this.bars[0].left} + 2%)`;
            this.characterBottom = this.bars[0].height;
            this.cdr.markForCheck();
          });
          this.currentStep = 0;
          this.climbTimeout = setTimeout(() => this.startClimbing(), 600);
        }, 3000);
      }, 900);
    }
  }
}
